<template>
  <the-dialog-box
    ref="dialogBox"
    :submit="submit"
    :max-width="700"
    :submit-btn-text="item.id ? 'Update' : 'Create'"
  >
    <template slot="header">
      <span>{{ item.id ? 'Update' : 'Create' }} Supplier</span>
    </template>
    <template slot="body">
      <v-row>
        <v-col class="pb-0 pt-5">
          <span style="font-size: 18px; font-weight: 400;">Main information</span>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="4"
          class="pb-0 pt-0"
        >
          <inventory-class-select-element
            v-model="item.inventory_class_ids"
            :multiple="true"
            :is-required="true"
            :is-add-all="false"
          />
        </v-col>
        <v-col
          cols="4"
          class="pb-0 pt-0"
        >
          <v-text-field
            v-model.trim="item.code"
            :rules="[(v) => !!v || 'Code is required', (v) => !!v && v.length <= 10 || 'Max 10 characters']"
            counter="10"
            clearable
          >
            <template slot="label">
              Code<RedAsterisk />
            </template>
          </v-text-field>
        </v-col>
        <v-col
          cols="4"
          class="pb-0 pt-0"
        >
          <v-text-field
            v-model.trim="item.name"
            :rules="[(v) => !!v || 'Name is required', (v) => !!v && v.length <= 50 || 'Max 50 characters']"
            counter="50"
            clearable
          >
            <template slot="label">
              Name<RedAsterisk />
            </template>
          </v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="pb-0">
          <v-text-field v-model="item.email" label="Email"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="4"
          class="pb-0 pt-0"
          @click="openContactForm"
        >
          <v-text-field
            @click="openContactForm"
            :value="contactString"
            readonly
            label="Contacts"
          />
        </v-col>
        <v-col
          cols="5"
          class="pb-0 pt-0"
        >
          <v-text-field
            v-model="item.phone_number"
            label="Phone Number"
            :rules="[(v) => (v === null || v.length <= 20) || 'Max 20 characters']"
            counter="20"
            clearable
          />
        </v-col>
        <v-col
          cols="3"
          class="pb-0 pt-0"
        >
          <v-text-field
            v-model="item.term"
            label="Term"
            type="string"
            :rules="[(v) => (v === null || v.length <= 5) || 'Max 5 characters']"
            counter="5"
            clearable
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col
          cols="6"
          class="pb-0 pt-0"
        >
          <v-select
            v-model="item.account_status"
            :items="accountStatusOptions"
            label="Account Status"
            clearable
          />
        </v-col>
        <v-col
          cols="6"
          class="pb-0 pt-0"
        >
          <v-select
            v-model="item.rank"
            :items="rankOptions"
            label="Rank"
            clearable
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="6"
          class="pb-0 pt-0"
        >
          <v-select
            v-model="item.classification"
            :items="classificationOptions"
            label="Classification"
            clearable
          />
        </v-col>
        <v-col
          cols="6"
          class="pb-0 pt-0"
        >
          <v-select
            v-model="item.risk_indicator"
            :items="riskIndicatorOptions"
            label="Risk Indicator"
            clearable
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col class="pb-0 pt-5">
          <span style="font-size: 18px; font-weight: 400;">Address</span>
        </v-col>
      </v-row>
      <v-row>
        <v-col
          cols="12"
          class="pb-0 pt-0"
        >
          <vuetify-google-autocomplete
            id="supplier-google-address"
            ref="googleAddress"
            v-model="googleAddress"
            :rules="[(v) => !!v || 'Address is required']"
            classname="form-control"
            :country="'ca'"
            @placechanged="getAddressData"
          >
            <template slot="label">
              Address<RedAsterisk />
            </template>
          </vuetify-google-autocomplete>
        </v-col>
      </v-row>
      <v-row v-if="getRoles.includes(userRoleIdAdmin) || getRoles.includes(userRoleIdProductionAdmin)">
        <v-col class="pb-0 pt-5">
          <span style="color: #000000; font-size: 18px; font-weight: 400;">Functionality</span>
        </v-col>
      </v-row>
      <v-row v-if="getRoles.includes(userRoleIdAdmin) || getRoles.includes(userRoleIdProductionAdmin)">
        <v-col>
          <v-select
            v-model="item.inventory_po_type_ids"
            :items="inventoryPoTypes"
            label="Inventory PO Types"
            item-text="name"
            item-value="id"
            persistent-hint
            multiple
            hint="IMPORTANT! Determines the type of Inventory PO that will be created when an order is placed for this supplier."
          >
            <template #item="{ item }">
              <v-chip
                class="mt-1 mb-1"
                small
                text-color="white"
                style="cursor: pointer;"
                :color="item ? item.color : ''"
              >
                {{ item ? item.name : '' }}
              </v-chip>
            </template>
            <template #selection="{ item }">
              <v-chip
                class="mt-1 mb-1"
                small
                text-color="white"
                style="cursor: pointer;"
                :color="item ? item.color : 'white'"
              >
                {{ item ? item.name : '' }}
              </v-chip>
            </template>
          </v-select>
        </v-col>
      </v-row>

      <v-row v-if="getRoles.includes(userRoleIdAdmin) || getRoles.includes(userRoleIdProductionAdmin)">
        <v-col>
          <v-select
            v-model="item.inventory_po_calculation_type_id"
            :items="inventoryPoSupplierCalculationTypes"
            label="Inventory PO Calculation Type"
            item-text="name"
            item-value="id"
            clearable
            persistent-hint
            hint="IMPORTANT! Determines the type of Inventory PO Calculation that will be applied when an order is placed for this supplier."
          >
            <template #item="{ item }">
              <v-row align="center">
                <v-col cols="auto">
                  <v-chip
                    class="mt-1 mb-1"
                    small
                    text-color="white"
                    style="cursor: pointer;"
                    :color="item ? item.color : ''"
                  >
                    {{ item ? item.name : '' }}
                  </v-chip>
                </v-col>
                <v-col>
                  <span style="font-weight: 600;">{{ item.formula }}</span>
                </v-col>
              </v-row>
            </template>
            <template #selection="{ item }">
              <v-row align="center">
                <v-col cols="auto">
                  <v-chip
                    class="mt-1 mb-1"
                    small
                    text-color="white"
                    style="cursor: pointer;"
                    :color="item ? item.color : ''"
                  >
                    {{ item ? item.name : '' }}
                  </v-chip>
                </v-col>
                <v-col>
                  <span style="font-weight: 600;">{{ item.formula }}</span>
                </v-col>
              </v-row>
            </template>
          </v-select>
        </v-col>
      </v-row>
      <v-dialog v-model="showContactForm" max-width="500px" :persistent="true">
        <ClientContactForm :contacts="contacts" @close="closeContactForm"/>
      </v-dialog>
    </template>
  </the-dialog-box>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import VuetifyGoogleAutocomplete from 'vuetify-google-autocomplete';
import * as FormatHelpers from '@/helpers/FormatHelpers';
import ClientContactForm from './clients/ClientContactForm.vue';
import * as supplierApi from '@/api/SupplierApi';

export default {
  components: {
    ClientContactForm,
    VuetifyGoogleAutocomplete,
  },
  props: {

  },
  data: () => ({
    item: null,
    googleAddress: null,
    selectedGoogleAddress: null,
    itemDto: {
      id: 0,
      name: null,
      code: null,
      email: null,
      phone_number: null,
      term: null,
      contact: null,
      inventory_class_ids: [],
      inventory_po_type_ids: [1],
      inventory_po_calculation_type_id: null,
      address: {
        unit: null,
        building: null,
        street: null,
        city: null,
        region_id: 9,
        postal_code: null,
        country_id: 1,
      },

      account_status: null,
      rank: null,
      classification: null,
      risk_indicator: null,
    },
    regions: [],
    countries: [],
    inventoryPoTypes: [],
    inventoryPoSupplierCalculationTypes: [],
    userRoleIdAdmin: 3,
    userRoleIdAssociate: 6,
    supplierTempId: null,
    contacts: [],
    showContactForm: false,
    inventoryPoTypeIdNew: 1,
    countryIdCanada: 1,
    regionIdOntario: 9,

    accountStatusOptions: [],
    classificationOptions: [],
    rankOptions: [],
    riskIndicatorOptions: [],
  }),
  computed: {
    ...mapState({
    }),
    ...mapGetters([
      'getRoles',
    ]),
    contactString() {
      return this.contacts.filter(x => !x.is_deleted).map(x => x.name).join(', ');
    }
  },
  watch: {

  },
  created() {
    this.item = { ...this.itemDto, address: { ...this.itemDto.address } };

    this.getCountries();
    this.getRegions();
    this.getInventoryPoTypes();
    this.getInventoryPoSupplierCalculationTypes();
    this.loadIsoOptions();
  },
  methods: {
    async loadIsoOptions() {
      try {
        const res = await this.$api.supplierIso.get();
        if (!res) return;
        this.accountStatusOptions = res.accountStatus || [];
        this.classificationOptions = res.classification || [];
        this.rankOptions = res.rank || [];
        this.riskIndicatorOptions = res.riskIndicator || [];
      } catch (e) {
        console.error('Failed to load supplier ISO options', e);
      }
    },

    async get(id) {
      if (!id) return;
      const submitModel = {
        filters: {
          id,
        },
      };
      const res = await this.$api.supplier.read(submitModel);
      if (!res || !res.data || !res.data[0]) return;
      const firstIndex = 0;
      this.item = res.data[firstIndex];

      if (!this.item.address || this.item.address.length === 0) {
        this.item.address = {
          unit: null,
          building: null,
          street: null,
          city: null,
          region_id: 9,
          postal_code: null,
          country_id: 1,
        };
      }
      this.googleAddress = this.item.full_address || null;
      this.selectedGoogleAddress = this.googleAddress;
    },

    async getCountries() {
      this.countries = await this.$api.country.get();
    },
    async getRegions() {
      this.regions = await this.$api.region.get();
    },

    async getInventoryPoTypes() {
      this.inventoryPoTypes = await this.$api.inventoryPoType.get();
    },

    async getInventoryPoSupplierCalculationTypes() {
      this.inventoryPoSupplierCalculationTypes = await this.$api.inventoryPoSupplierCalculationType.get();
    },

    async getSupplierContacts() {
      const submitModel = {
        filters: {
          supplier_id: this.item.id ? this.item.id : this.supplierTempId,
        },
      };
      const res = await this.$api.supplierContact.read(submitModel);
      if (!res || res.length < 1 || !res[0]) return;
      this.contacts = res;
      console.log(res)
    },

    validateAddress() {
      if (!this.googleAddress || this.googleAddress !== this.selectedGoogleAddress) {
        this.$bus.$emit('showError', 'Please select an address from the suggestions.');
        return false;
      }
      const missingAddressFields = [];
      if (!this.item.address.building) missingAddressFields.push('building number');
      if (!this.item.address.street) missingAddressFields.push('street name');
      if (!this.item.address.city) missingAddressFields.push('city');
      if (!this.item.address.region_id) missingAddressFields.push('province or state');
      if (!this.item.address.country_id) missingAddressFields.push('country');

      if (!missingAddressFields.length) return true;

      const addressErrorMessage = missingAddressFields.length === 5
        ? 'Please select an address.'
        : `Please complete the address: ${missingAddressFields.join(', ')}.`;

      this.$bus.$emit('showError', addressErrorMessage);
      return false;
    },

    async submit() {
      if (!this.item) return false;
      if (!this.validateAddress()) return false;
      let res;
      this.item.contacts = this.contacts;
      try {
        res = this.item.id ? await supplierApi.update(this.item) : await supplierApi.create(this.item);
        this.$bus.$emit('showSuccess');
        this.$emit('success');
        return res;
      } catch (e) {
        console.error(e);
        this.$bus.$emit('showError', `Failed to save the Supplier. ${e.response?.data ?? ''}`);
      }
    },

    async open(id) {
      if (id) {
        this.item.id = id;
        this.get(id);
      } else {
        this.item = { ...this.itemDto, address: { ...this.itemDto.address } };
        this.item.inventory_po_type_ids = [this.inventoryPoTypeIdNew];
        this.googleAddress = null;
        this.selectedGoogleAddress = null;
      }
      this.$refs.dialogBox.open();
      if (!this.item.address.id) {
        this.item.address.region_id = this.regionIdOntario;
        this.item.address.country_id = this.countryIdCanada;
      }
      const maxId = await this.$api.supplier.get('max-id');
      this.supplierTempId = maxId + 1;
      this.getSupplierContacts();
    },

    normalizeAddressField(field) {
      return field ? field.toString().trim().toLowerCase() : '';
    },

    getAddressData(addressData, placeData) {
      console.log('addressData', addressData);
      console.log('placeData', placeData);
      const countryName = this.normalizeAddressField(addressData.country);
      const country = this.countries.find((item) => (
        this.normalizeAddressField(item.name) === countryName
        || this.normalizeAddressField(item.abbreviation) === countryName
      ));
      const regionName = this.normalizeAddressField(addressData.administrative_area_level_1);
      const region = this.regions.find((item) => (
        (!country || String(item.country_id) === String(country.id))
        && (
          this.normalizeAddressField(item.name) === regionName
          || this.normalizeAddressField(item.abbreviation) === regionName
        )
      ));

      const unitComponent = (placeData.address_components || []).find((component) => (
        component.types.includes('subpremise')
      ));

      this.googleAddress = placeData.formatted_address;
      this.selectedGoogleAddress = this.googleAddress;
      this.item.address = {
        ...this.item.address,
        unit: unitComponent && unitComponent.long_name.toLowerCase().includes('unit')
          ? unitComponent.long_name.replace(/unit/i, '').trim() : null,
        building: addressData.street_number || '',
        street: addressData.route || '',
        city: addressData.locality || addressData.administrative_area_level_2 || '',
        region_id: region ? region.id : this.regionIdOntario,
        country_id: country ? country.id : this.countryIdCanada,
        postal_code: addressData.postal_code || '',
      };
    },

    openContactForm() {
      this.showContactForm = true;
    },
    closeContactForm() {
      this.showContactForm = false;
      this.contacts = this.contacts.filter(x => !!x.id || !!x.name);
      this.contacts.filter(x => !x.name).forEach(x => x.is_deleted = 1);
    },

    formatPhoneNumber() {
      this.item.phone_number = FormatHelpers.formatPhoneNumber(this.item.phone_number);
    },
  },
};
</script>

<style scoped>
/deep/#supplier-google-address {
  width: 100%;
}
</style>
